import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, X, Users, MessageSquare, UserPlus, BookOpen } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import CoachFeedSection from './CoachFeedSection';

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, loading } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Typed fallback: when a notification arrives without an explicit
  // link, derive one from the type so clicks never go nowhere.
  const resolveFallbackLink = (n) => {
    switch (n.type) {
      case 'group_join':
      case 'group_message':
      case 'group_announcement':
      case 'group_invite':
        return n.group_id ? `/study-groups/${n.group_id}` : '/study-groups';
      case 'lecture_shared':
        return n.lecture_id ? `/lectures?lecture=${n.lecture_id}` : '/lectures';
      case 'streak_at_risk':
      case 'flashcards_due':
        return '/review';
      case 'comment':
      case 'reaction':
        return n.post_id ? `/feed?post=${n.post_id}` : '/feed';
      default:
        return null;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'group_join':
        return <Users className="w-4 h-4 text-green-400" />;
      case 'group_message':
      case 'group_announcement':
        return <MessageSquare className="w-4 h-4 text-blue-400" />;
      case 'group_invite':
        return <UserPlus className="w-4 h-4 text-purple-400" />;
      case 'lecture_shared':
        return <BookOpen className="w-4 h-4 text-yellow-400" />;
      default:
        return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-xl hover:bg-[var(--isp-glass-inner)] transition-colors"
        data-testid="notification-bell"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Bell className="w-5 h-5 text-[var(--isp-body)]" aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white" aria-hidden>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-slate-900 border border-[var(--isp-glass-border)]  shadow-2xl overflow-hidden z-[100]"
          >
            {/* Header */}
            <div className="p-4 border-b border-[var(--isp-glass-border)] flex items-center justify-between">
              <h3 className="font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
            </div>

            {/* iter193 — curated next-best-action coach feed sits ABOVE
                the system event log so users see "what to do next" first,
                "what happened" second. */}
            <CoachFeedSection onItemNavigate={() => setIsOpen(false)} />

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mx-auto" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-10 h-10 text-white/20 mx-auto mb-2" />
                  <p className="text-[var(--isp-body)] text-sm">No notifications yet</p>
                </div>
              ) : (
                notifications.slice(0, 20).map((notification, idx) => (
                  <div
                    key={`${notification.id || 'notif'}-${idx}`}
                    className={`p-4 border-b border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner)] transition-colors cursor-pointer ${
                      !notification.read ? 'bg-purple-500/5' : ''
                    }`}
                    onClick={() => {
                      if (!notification.read) {
                        markAsRead(notification.id);
                      }
                      // Resolve target route. Notifications may carry `link`,
                      // `action_url`, or a typed fallback derived from the
                      // notification.type so users never click into a void.
                      const link = notification.link || notification.action_url || resolveFallbackLink(notification);
                      setIsOpen(false);
                      if (link) {
                        // Use SPA navigation (react-router) for in-app links,
                        // fallback to hard nav only for absolute externals.
                        if (/^https?:\/\//i.test(link)) {
                          window.open(link, '_blank', 'noopener');
                        } else {
                          navigate(link);
                        }
                      }
                    }}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!notification.read ? 'text-white' : 'text-[var(--isp-body)]'}`}>
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-[var(--isp-body)] mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-[var(--isp-muted)] mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-[var(--isp-glass-border)] text-center">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    // Could navigate to a full notifications page
                  }}
                  className="text-xs text-purple-400 hover:text-purple-300"
                >
                  View all notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
